import { BaseService } from './BaseService';
import { httpClient } from '../services/HttpClient';
import type {
  Message,
  Conversation,
  ConversationsResponse,
  ConversationHistoryResponse,
  HistoryMessage,
  ChatRequest,
  MediaFile,
  SSEStreamEvent,
  SSEEvent,
  MessageAdditionalKwargs,
} from '../types/api.types';
import { API_ENDPOINTS } from '../config/api.config';
import { conversationService } from '@/shared/services/ConversationService';

export interface StreamCallbacks {
  onMessage: (content: string) => void;
  onMetadata?: (runId: string) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

export interface WebSearchRequest {
  input: {
    question: string;
    k: number;
    system_prompt: string;
  };
  config: {
    configurable: {
      conversation_id: string;
      provider: string;
      user_id: string;
      [key: string]: string | number;
    };
  };
  kwargs: Record<string, unknown>;
}

export class ChatService extends BaseService {
  private userId: string | undefined;
  private activeStreams: Map<string, AbortController> = new Map();

  setUserId(userId: string | undefined) {
    this.userId = userId;
  }

  // Build full thread id including user prefix when only uuid is provided
  private buildFullThreadId(conversationId: string): string {
    if (!conversationId) return conversationId;
    // If it already looks like a full thread id (contains an email), return as-is
    if (conversationId.includes('@')) return conversationId;
    // Use user ID to build full thread ID
    if (this.userId) return `${this.userId}_${conversationId}`;
    return conversationId;
  }

  // Strip user prefix from a thread id for public/bare representation
  private stripUserPrefix(threadId: string, userId?: string): string {
    if (!threadId) return threadId;
    if (userId && threadId.startsWith(`${userId}_`)) {
      return threadId.substring(userId.length + 1);
    }
    const idx = threadId.lastIndexOf('_');
    return idx >= 0 ? threadId.substring(idx + 1) : threadId;
  }

  async getConversations(): Promise<Conversation[]> {
    // Try to fetch with new format first
    const response = await this.get<ConversationsResponse | Conversation[]>(
      API_ENDPOINTS.CONVERSATIONS,
      { useUserId: true, userId: this.userId }
    );

    if (!response) {
      return [];
    }

    // Check if it's the new wrapped format
    if ('conversations' in response && Array.isArray(response.conversations)) {
      // Map the new format to the old format for backward compatibility
      return response.conversations.map(conv => {
        const bareId = this.stripUserPrefix(conv.thread_id, response.user_id);
        return {
          _id: bareId, // Use bare id for UI/URL
          thread_id: conv.thread_id, // Keep full id for reference when needed
          title: conv.title,
          checkpoint_count: conv.checkpoint_count,
          created_at: conv.created_at,
          updated_at: conv.updated_at,
        };
      });
    }

    // Fall back to old format if it's an array (backward compatibility)
    if (Array.isArray(response)) {
      return response;
    }

    return [];
  }

  async getConversationHistory(conversationId: string): Promise<Message[]> {
    const fullId = this.buildFullThreadId(conversationId);
    const response = await this.get<ConversationHistoryResponse>(
      API_ENDPOINTS.CONVERSATION(fullId),
      { useUserId: true, userId: this.userId }
    );

    if (!response || !response.history) {
      return [];
    }

    // Transform new format to existing Message format for compatibility
    const messages: Message[] = response.history.map((msg: HistoryMessage) => {
      const additionalKwargs: MessageAdditionalKwargs = {};

      if (msg.uploaded_files?.length) {
        additionalKwargs.uploaded_files = msg.uploaded_files;
      }

      if (typeof msg.has_sources === 'boolean') {
        additionalKwargs.has_sources = msg.has_sources;
      }

      if (msg.web_search_sources?.length) {
        additionalKwargs.web_search_sources = msg.web_search_sources;
        // Mirror new structure to legacy field for backward compatibility
        additionalKwargs.web_results = msg.web_search_sources;
      }

      if (msg.thinking_steps?.length) {
        additionalKwargs.thinking_steps = msg.thinking_steps;
      }

      if (msg.rag_sources?.length) {
        additionalKwargs.rag_sources = msg.rag_sources;
      }

      if (msg.deepResearchContext) {
        additionalKwargs.deepResearchContext = msg.deepResearchContext;
      }

      return {
        type: msg.role === 'user' ? 'human' : 'ai',
        data: {
          content: msg.content,
          additional_kwargs: additionalKwargs,
          response_metadata: {},
          type: msg.role === 'user' ? 'human' : 'ai',
          name: null,
          id: msg.id,
          example: false,
          tool_calls: undefined,
          invalid_tool_calls: undefined,
          usage_metadata: null,
        },
      };
    });

    return messages;
  }

  async deleteConversation(conversationId: string): Promise<boolean> {
    const fullId = this.buildFullThreadId(conversationId);
    const response = await this.delete<{ message: string }>(
      API_ENDPOINTS.CONVERSATION(fullId),
      { useUserId: true, userId: this.userId }
    );
    return !!response;
  }

  async updateConversationTitle(
    conversationId: string,
    title: string
  ): Promise<boolean> {
    try {
      const fullId = this.buildFullThreadId(conversationId);

      // Try to get the conversation detail to find the assistant_id
      // Use the bare conversationId (without user prefix) for the API call
      let assistantId = 266; // Default fallback value
      try {
        const bareConversationId = this.stripUserPrefix(
          conversationId,
          this.userId
        );
        const conversationDetail =
          await conversationService.getConversationDetail(bareConversationId);
        if (
          conversationDetail?.assistant_detail?.id !== undefined &&
          conversationDetail.assistant_detail.id !== null
        ) {
          assistantId = conversationDetail.assistant_detail.id;
        }
      } catch {
        // If we can't get conversation detail, fall back to default
      }

      const response = await this.put<{ message: string; title: string }>(
        API_ENDPOINTS.CONVERSATION_UPDATE_TITLE(fullId),
        { new_title: title },
        {
          useUserId: true,
          userId: this.userId,
          headers: {
            assistant_id: String(assistantId),
          },
        }
      );
      return !!response;
    } catch (error) {
      console.error('Failed to update conversation title:', error);
      return false;
    }
  }

  async retryConversation(conversationId: string): Promise<{
    success: boolean;
    thread_id: string;
    removed_count: number;
    remaining_count: number;
  }> {
    const fullId = this.buildFullThreadId(conversationId);
    const response = await this.post<{
      success: boolean;
      thread_id: string;
      removed_count: number;
      remaining_count: number;
    }>(
      API_ENDPOINTS.CONVERSATION_RETRY(fullId),
      {},
      { useUserId: true, userId: this.userId }
    );
    return (
      response || {
        success: false,
        thread_id: fullId,
        removed_count: 0,
        remaining_count: 0,
      }
    );
  }

  async streamMultimodalChat(
    question: string,
    config: ChatRequest['config'],
    files: File[],
    callbacks: StreamCallbacks
  ): Promise<() => void> {
    // Validate file content before sending
    for (const file of files) {
      if (file.size === 0) {
        console.error(`[ChatService] File "${file.name}" has zero size!`);
        callbacks.onError?.(new Error(`File "${file.name}" is empty`));
        return () => {};
      }

      // Try to read a small chunk to verify file is readable
      try {
        const slice = file.slice(0, Math.min(100, file.size));
        const reader = new FileReader();

        await new Promise<void>((resolve, reject) => {
          reader.onload = () => {
            resolve();
          };
          reader.onerror = () => {
            console.error(
              `[ChatService] Failed to read file "${file.name}":`,
              reader.error
            );
            reject(reader.error);
          };
          reader.readAsArrayBuffer(slice);
        });
      } catch (error) {
        console.error(
          `[ChatService] Error validating file "${file.name}":`,
          error
        );
        callbacks.onError?.(new Error(`Failed to read file "${file.name}"`));
        return () => {};
      }
    }

    const conversationId = this.buildFullThreadId(config.conversation_id);

    // Cancel any existing stream for this conversation
    this.cancelStream(conversationId);

    // Create new abort controller
    const abortController = new AbortController();
    this.activeStreams.set(conversationId, abortController);

    // Use the multimodal endpoint
    const endpoint = API_ENDPOINTS.CHAT_MULTIMODAL_STREAM;

    // Prepare the data for multipart/form-data
    // The HttpClient will extract 'files' and stringify the rest as json_input
    // This creates the ChatRequest structure: { question, config }
    const requestData = {
      question,
      config: {
        ...config,
        conversation_id: conversationId, // Use full conversation ID with user prefix (email_uuid format)
      },
      files, // Will be extracted by HttpClient and sent as separate form fields
    };

    // Start streaming with multipart/form-data
    const streamPromise = httpClient.streamPostWithFiles(
      endpoint,
      requestData,
      (data: unknown) => {
        // Handle both new and legacy SSE event formats
        const event = data as SSEStreamEvent | SSEEvent;

        // Check if it's the new format with type field
        if ('type' in event && event.type) {
          const typedEvent = event as SSEStreamEvent;
          switch (typedEvent.type) {
            case 'session_start':
              callbacks.onMetadata?.(typedEvent.conversation_id);
              break;
            case 'token':
              // Extract text from content array if it's an array (like in ChatSessionService)
              if (Array.isArray(typedEvent.content)) {
                const text = typedEvent.content
                  .map((item: { text?: string }) => item.text || '')
                  .join('');
                callbacks.onMessage(text);
              } else {
                callbacks.onMessage(typedEvent.content);
              }
              break;
            case 'complete':
              // Stream complete
              break;
          }
        }
        // Legacy format handling
        else {
          const legacyEvent = event as SSEEvent;
          if ('answer' in legacyEvent) {
            callbacks.onMessage(legacyEvent.answer);
          } else if ('run_id' in legacyEvent) {
            callbacks.onMetadata?.(legacyEvent.run_id);
          }
        }
      },
      (error: Error) => {
        callbacks.onError?.(error);
        this.activeStreams.delete(conversationId);
      },
      false, // isRetry
      {
        fileFieldName: 'files', // Use 'files' as the field name for all files
        jsonFieldName: 'json_input', // Use 'json_input' for the ChatRequest JSON
      }
    );

    streamPromise
      .then(() => {
        callbacks.onComplete?.();
        this.activeStreams.delete(conversationId);
      })
      .catch((error: Error) => {
        callbacks.onError?.(error);
        this.activeStreams.delete(conversationId);
      });

    // Return cancel function
    return () => this.cancelStream(conversationId);
  }

  async streamChat(
    request: ChatRequest | WebSearchRequest,
    callbacks: StreamCallbacks,
    _options?: { isWebSearch?: boolean }
  ): Promise<() => void> {
    // Handle both old and new request formats
    const conversationId =
      'config' in request && 'configurable' in request.config
        ? request.config.configurable.conversation_id
        : (request as ChatRequest).config.conversation_id;
    const fullId = this.buildFullThreadId(conversationId);

    // Cancel any existing stream for this conversation
    this.cancelStream(conversationId);

    // Create new abort controller
    const abortController = new AbortController();
    this.activeStreams.set(fullId, abortController);

    // Always use the main chat/stream endpoint now
    const endpoint = API_ENDPOINTS.CHAT_STREAM;

    // Start streaming - use regular JSON since media files are base64 encoded
    const streamPromise = httpClient.streamPost(
      endpoint,
      // Ensure the request contains the full id
      {
        ...request,
        config: {
          ...request.config,
          conversation_id: fullId,
        },
      },
      (data: unknown) => {
        // Handle both new and legacy SSE event formats
        const event = data as SSEStreamEvent | SSEEvent;

        // Check if it's the new format with type field
        if ('type' in event && event.type) {
          const typedEvent = event as SSEStreamEvent;
          switch (typedEvent.type) {
            case 'session_start':
              // Could store session metadata if needed
              callbacks.onMetadata?.(typedEvent.conversation_id);
              break;
            case 'token':
              // Extract text from content array if it's an array (like in ChatSessionService)
              if (Array.isArray(typedEvent.content)) {
                const text = typedEvent.content
                  .map((item: { text?: string }) => item.text || '')
                  .join('');
                callbacks.onMessage(text);
              } else {
                // Pass through the content as-is if it's already a string
                callbacks.onMessage(typedEvent.content);
              }
              break;
            case 'complete':
              // Stream is complete, onComplete will be called by the promise resolution
              break;
          }
        }
        // Legacy format (for backward compatibility)
        else {
          const legacyEvent = event as SSEEvent;
          if ('answer' in legacyEvent) {
            callbacks.onMessage(legacyEvent.answer);
          } else if ('run_id' in legacyEvent) {
            callbacks.onMetadata?.(legacyEvent.run_id);
          }
        }
      },
      (error: Error) => {
        callbacks.onError?.(error);
        this.activeStreams.delete(fullId);
      },
      true // useUserId flag for chat stream
    );

    streamPromise
      .then(() => {
        callbacks.onComplete?.();
        this.activeStreams.delete(fullId);
      })
      .catch((error: Error) => {
        callbacks.onError?.(error);
        this.activeStreams.delete(fullId);
      });

    // Return cancel function
    return () => this.cancelStream(fullId);
  }

  cancelStream(conversationId: string): void {
    const controller = this.activeStreams.get(conversationId);
    if (controller) {
      controller.abort();
      this.activeStreams.delete(conversationId);
    }
  }

  cancelAllStreams(): void {
    this.activeStreams.forEach(controller => controller.abort());
    this.activeStreams.clear();
  }

  // Helper method to build web search request (now uses same structure with flag)
  buildWebSearchRequest(
    question: string,
    conversationId: string,
    _userId: string,
    options: {
      modelName: string;
      provider: string;
      systemPrompt?: string;
      temperature?: number;
      k?: number;
    }
  ): ChatRequest {
    const fullId = this.buildFullThreadId(conversationId);
    return {
      question,
      config: {
        model_provider: options.provider,
        model_name: options.modelName,
        conversation_id: fullId,
        temperature: options.temperature || 0.2,
        system_prompt: options.systemPrompt || 'You are a helpful assistant',
        web_search_enabled: true,
        rag_enabled: false,
        k: options.k || 10,
      },
    };
  }

  // Helper method to build chat request (new simplified format)
  buildChatRequest(
    question: string,
    conversationId: string,
    _userId: string,
    options: {
      modelName: string;
      provider: string;
      systemPrompt?: string;
      temperature?: number;
      k?: number;
      botId?: string;
      dataStores?: string[];
      isDefaultBot?: boolean;
    },
    mediaFiles?: MediaFile[],
    isWebSearchEnabled?: boolean,
    _chatSource?: 'sidebar' | 'assistant' | null
  ): ChatRequest {
    const fullId = this.buildFullThreadId(conversationId);
    // Convert media files to file URLs if needed
    // Note: In a real implementation, these would need to be uploaded first
    // to get actual URLs. For now, we'll pass them as data URLs.
    const fileUrls = mediaFiles?.map(
      file => `data:${file.mime_type};base64,${file.file_data}`
    );

    // Determine flags based on web search and bot type
    // rag_enabled should be true only when bot exists and is not a default bot
    const webSearchEnabled = isWebSearchEnabled || false;
    const ragEnabled = Boolean(
      options.botId &&
        options.isDefaultBot !== undefined &&
        !options.isDefaultBot
    );

    const request: ChatRequest = {
      question,
      config: {
        model_provider: options.provider,
        model_name: options.modelName,
        conversation_id: fullId,
        temperature: options.temperature || 0.7,
        system_prompt: options.systemPrompt || 'You are a helpful assistant',
        rag_enabled: ragEnabled,
        web_search_enabled: webSearchEnabled,
        k: options.k || 5,
        store_ids: options.dataStores || [],
      },
      file_urls: fileUrls,
    };

    return request;
  }

  clearCache(): void {}

  // Helper method to handle streaming with state management
  async sendMessageWithStreaming(
    text: string,
    conversationId: string,
    userId: string,
    modelOptions: {
      modelName: string;
      provider: string;
      systemPrompt?: string;
      temperature?: number;
      k?: number;
      botId?: string;
      dataStores?: string[];
      isDefaultBot?: boolean;
    },
    stateCallbacks: {
      onStreamUpdate: (content: string) => void;
      onComplete: (finalText: string) => void;
      onError: (error: Error) => void;
    },
    mediaFiles?: MediaFile[],
    isWebSearch?: boolean,
    files?: File[], // Add native File objects for multimodal
    chatSource?: 'sidebar' | 'assistant' | null
  ): Promise<void> {
    const fullId = this.buildFullThreadId(conversationId);
    let accumulatedText = '';
    // Check if we have files for multimodal chat
    if (files && files.length > 0 && !isWebSearch) {
      // Use multimodal endpoint for file uploads
      // Determine flags based on web search and bot type
      // rag_enabled should be true only when bot exists and is not a default bot
      const webSearchEnabled = isWebSearch || false;
      const ragEnabled = Boolean(
        modelOptions.botId &&
          modelOptions.isDefaultBot !== undefined &&
          !modelOptions.isDefaultBot
      );

      const config: ChatRequest['config'] = {
        model_provider: modelOptions.provider,
        model_name: modelOptions.modelName,
        conversation_id: fullId,
        temperature: modelOptions.temperature || 0.7,
        system_prompt:
          modelOptions.systemPrompt || 'You are a helpful assistant',
        rag_enabled: ragEnabled,
        web_search_enabled: webSearchEnabled,
        k: modelOptions.k || 5,
        store_ids: modelOptions.dataStores || [],
      };

      await this.streamMultimodalChat(text, config, files, {
        onMessage: (content: string) => {
          accumulatedText += content;
          stateCallbacks.onStreamUpdate(content);
        },
        onComplete: () => {
          stateCallbacks.onComplete(accumulatedText);
        },
        onError: (error: Error) => {
          stateCallbacks.onError(error);
        },
      });
    } else {
      // Use regular chat endpoint for text-only or web search
      let request: ChatRequest;

      if (isWebSearch) {
        request = this.buildWebSearchRequest(
          text,
          fullId,
          userId,
          modelOptions
        );
      } else {
        request = this.buildChatRequest(
          text,
          fullId,
          userId,
          modelOptions,
          mediaFiles,
          isWebSearch,
          chatSource
        );
      }

      await this.streamChat(
        request,
        {
          onMessage: (content: string) => {
            accumulatedText += content;
            stateCallbacks.onStreamUpdate(content);
          },
          onComplete: () => {
            stateCallbacks.onComplete(accumulatedText);
          },
          onError: (error: Error) => {
            stateCallbacks.onError(error);
          },
        },
        { isWebSearch }
      );
    }
  }
}

export const chatService = new ChatService();
